import { Pawn } from "./board/Pawn";
import { getThemeColors } from "../lib/theme";
import { formatDurationCompact } from "../lib/utils/time";
import { useTranslation } from "react-i18next";
import { ProviderBadge } from "./ProviderBadge";
import { ProfileAvatar } from "./ProfileAvatar";

export interface LeaderboardEntry {
  name: string;
  /** Provider of the player's most recent sign-in, rendered as the badge. */
  provider?: string | null;
  image?: string | null;
  time: number;
  clicks: number;
  meta?: string; // Additional info like "3/5" for found/target count
  isCurrentUser?: boolean;
}

interface LeaderboardProps {
  entries: LeaderboardEntry[];
  isDark: boolean;
  theme: ReturnType<typeof getThemeColors>;
  emptyMessage?: string;
  maxItems?: number;
  showDividers?: boolean;
  rounded?: boolean;
}

export function Leaderboard({
  entries,
  isDark,
  theme,
  emptyMessage,
  maxItems = 20,
  showDividers = true,
  rounded = false,
}: LeaderboardProps) {
  const { t } = useTranslation();
  const displayEntries = entries.slice(0, maxItems);

  if (displayEntries.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 p-8 text-center">
        <Pawn hopping className="h-10 w-10" />
        <p className={`font-display text-base ${theme.text}`}>
          {emptyMessage ?? t("components.leaderboard.empty")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {displayEntries.map((entry, index) => (
        <LeaderboardRow
          key={entry.name + index}
          entry={entry}
          index={index}
          isDark={isDark}
          theme={theme}
          showDivider={showDividers && index !== 0}
          rounded={rounded}
        />
      ))}
    </div>
  );
}

interface LeaderboardRowProps {
  entry: LeaderboardEntry;
  index: number;
  isDark: boolean;
  theme: ReturnType<typeof getThemeColors>;
  showDivider?: boolean;
  rounded?: boolean;
}

export function LeaderboardRow({
  entry,
  index,
  isDark,
  theme,
  showDivider = true,
  rounded = false,
}: LeaderboardRowProps) {
  // Podium places are tokens: gold, cream (silver) and bronze.
  const getRankStyle = () => {
    if (index === 0) return "bg-gold text-ink border-ink";
    if (index === 1) return "bg-cream text-ink border-ink";
    if (index === 2) return "bg-[#e8894a] text-ink border-ink";
    return `bg-transparent border-transparent ${theme.textMuted}`;
  };

  const rowClasses = rounded
    ? `flex items-center gap-4 p-4 ${entry.isCurrentUser ? theme.rowActive : theme.row}`
    : `flex items-center gap-4 p-4 transition-all duration-200 ${
        showDivider ? `border-t-2 ${theme.divider}` : ""
      } ${isDark ? "hover:bg-zinc-800/50" : "hover:bg-gray-100"}`;

  return (
    <div className={rowClasses}>
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-[3px] font-display text-sm ${getRankStyle()}`}
      >
        {index + 1}
      </div>

      <div className="flex min-w-0 flex-1 items-center gap-2">
        <ProfileAvatar image={entry.image} name={entry.name} size="sm" />
        <div
          className={`truncate ${entry.isCurrentUser ? "font-semibold" : ""} ${theme.text}`}
        >
          {entry.name}
        </div>
        <ProviderBadge provider={entry.provider} />
      </div>

      <div className={`text-right tabular-nums font-mono ${theme.textMuted}`}>
        {formatDurationCompact(entry.time)}
      </div>

      <div
        className={`text-right w-16 font-mono text-sm ${theme.textSecondary}`}
      >
        {entry.meta || entry.clicks}
      </div>
    </div>
  );
}
